import React from "react";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";

const Solutions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const containerStyle = {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: isMobile ? "3rem 1.5rem" : isTablet ? "4.5rem 2rem" : "6rem 4rem",
  };

  const sectionTitle = {
    fontSize: isMobile ? "26px" : "38px",
    fontWeight: 700,
    color: "#1E1B4B",
    marginBottom: "20px",
    textAlign: "center",
  };

  const paragraphStyle = {
    fontSize: isMobile ? "14px" : "16px",
    lineHeight: "28px",
    color: "#4B5563",
  };

  const solutionCard = {
    padding: "30px",
    borderRadius: "12px",
    border: "1px solid #E9E6F5",
    backgroundColor: "#ffffff",
    transition: "all 0.3s ease",
  };

  const solutionTitle = {
    fontSize: isMobile ? "18px" : "22px",
    fontWeight: 600,
    color: "#6A0DAD",
    marginBottom: "15px",
  };

  const industriesGrid = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "repeat(2, 1fr)"
      : "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "20px",
    marginTop: "40px",
  };

  const industryItem = {
    padding: "15px 20px",
    backgroundColor: "#F5F3FF",
    borderRadius: "8px",
    fontWeight: 500,
    color: "#1E1B4B",
  };

  const processGrid = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
    gap: "40px",
    marginTop: "40px",
  };

  return (
    <>
      <section
        style={{
          background: "linear-gradient(135deg, #0F0A1F, #240046)",
          color: "#ffffff",
          padding: isMobile ? "4rem 1.5rem" : isTablet ? "5rem 2rem" : "6rem 4rem",
          textAlign: "center",
        }}
      >
        <h1
          style={{
            fontSize: isMobile ? "30px" : "48px",
            fontWeight: 700,
          }}
        >
          Integrated Technology Solutions
        </h1>

        <p
          style={{
            maxWidth: "800px",
            margin: "20px auto 0",
            fontSize: isMobile ? "15px" : "18px",
            color: "#DCD6F7",
            lineHeight: "28px",
          }}
        >
          We design and integrate complete infrastructure environments across
          data centres, enterprise networks, Wi-Fi, security systems, and
          electronics hardware, engineered for reliability and scale.
        </p>
      </section>

      <section style={containerStyle}>
        <h2 style={sectionTitle}>Our Core Capabilities</h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : isTablet
              ? "repeat(2, 1fr)"
              : "repeat(auto-fit, minmax(300px, 1fr))",
            gap: isMobile ? "20px" : "30px",
            marginTop: "50px",
          }}
        >
          {[
            {
              title: "Data Centre Infrastructure",
              desc: "Server room and data centre setup including racks, cabling, power distribution, cooling coordination, and deployment planning.",
            },
            {
              title: "Enterprise Wi-Fi & Networking",
              desc: "Campus networking, wireless surveys, controller-based Wi-Fi, switching, routing, firewalling, and secure connectivity solutions.",
            },
            {
              title: "Structured Cabling & Electronics",
              desc: "Copper and fiber cabling, patching, terminations, hardware installation, field electronics integration, and site readiness work.",
            },
            {
              title: "Surveillance & Access Systems",
              desc: "Integrated CCTV, access control, monitoring hardware, and supporting network infrastructure for secure facilities.",
            },
            {
              title: "Managed Support & AMC",
              desc: "Ongoing maintenance, fault response, preventive checks, upgrades, and lifecycle support for deployed infrastructure.",
            },
          ].map((item, index) => (
            <div
              key={index}
              style={solutionCard}
              onMouseEnter={(e) =>
                (e.currentTarget.style.boxShadow =
                  "0 10px 25px rgba(106, 13, 173, 0.15)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.boxShadow = "none")
              }
            >
              <h3 style={solutionTitle}>{item.title}</h3>
              <p style={{ ...paragraphStyle, margin: 0 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section style={{ ...containerStyle, paddingTop: 0 }}>
        <h2 style={sectionTitle}>Industries We Serve</h2>

        <div style={industriesGrid}>
          {[
            "Corporate Offices",
            "Data Centres",
            "Hospitals & Campuses",
            "Warehouses & Plants",
            "Government Projects",
            "Commercial Buildings",
          ].map((industry, index) => (
            <div key={index} style={industryItem}>
              {industry}
            </div>
          ))}
        </div>
      </section>

      <section style={{ ...containerStyle, paddingTop: 0 }}>
        <h2 style={sectionTitle}>How We Work</h2>

        <div style={processGrid}>
          {[
            {
              title: "Assess & Design",
              desc: "Site study, requirement mapping, BoQ support, and solution architecture aligned to operational goals.",
            },
            {
              title: "Deploy & Integrate",
              desc: "Procurement coordination, installation, configuration, testing, and handover across all solution layers.",
            },
            {
              title: "Support & Optimize",
              desc: "Maintenance, upgrades, troubleshooting, and performance optimization after go-live.",
            },
          ].map((step, index) => (
            <div key={index}>
              <h4
                style={{
                  fontWeight: 600,
                  color: "#6A0DAD",
                  marginBottom: "10px",
                }}
              >
                {step.title}
              </h4>
              <p style={{ ...paragraphStyle, margin: 0 }}>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section
        style={{
          padding: isMobile ? "3rem 1.5rem" : "5rem 4rem",
          backgroundColor: "#F5F3FF",
          textAlign: "center",
        }}
      >
        <h3
          style={{
            fontWeight: 700,
            marginBottom: "20px",
            color: "#1E1B4B",
          }}
        >
          Build Your Infrastructure with One Integration Partner
        </h3>

        <a
          href="/contact"
          style={{
            background: "linear-gradient(90deg, #6A0DAD, #9D4EDD)",
            padding: isMobile ? "12px 22px" : "12px 30px",
            borderRadius: "8px",
            color: "#ffffff",
            textDecoration: "none",
            fontWeight: 600,
            boxShadow: "0 8px 20px rgba(157, 78, 221, 0.3)",
            display: "inline-block",
            width: isMobile ? "100%" : "auto",
            maxWidth: isMobile ? "280px" : "none",
          }}
        >
          Talk to Our Team
        </a>
      </section>
    </>
  );
};

export default Solutions;
